import { action } from "typesafe-actions";
import { IBusinessPolicyRiskLocation } from "./businessPolicyRiskLocation.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { buildBinderRequest } from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-business-tabs
// Optional lead scope for the Risk Location binder sub-tab. When supplied, the thunks
// below fetch/save keyed by `lead_id` (no `customer_policy_id`) and re-fetch by
// `lead_id` after a successful save. Endpoint selection is routed through
// `buildBinderRequest(..., "risk-location", ...)` so the backend contract lives in one
// place. When absent, the original customer-policy path is used byte-for-byte.
export interface ILeadRiskLocationScope {
  leadId: string;
}

// 30s client-side bound so lead-scoped fetch/save surface the error + retry path
// deterministically (Requirements 3.5, 3.6).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

// Guard preventing a duplicate lead-scoped save from being dispatched while a save for
// the same record is already in flight (Requirement 3.8).
let leadRiskLocationSaveInFlight = false;

export const FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_FAILED";

export const fetchBusinessPolicyRiskListProgress = () =>
  action(FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_PROGRESS);
export const fetchBusinessPolicyRiskListSuccess = (
  data: IBusinessPolicyRiskLocation[]
) => action(FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_SUCCESS, { data });
export const fetchBusinessPolicyRiskListFailed = () =>
  action(FETCH_BUSINESS_POLICY_RISK_LOCATION_LIST_FAILED);

export const fetchBusinessPolicyRiskListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRiskListProgress());
      let finalUrl = `/policy/get-policy-business-risk-location?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyRiskLocation[] = res.data.data;
      dispatch(fetchBusinessPolicyRiskListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyRiskListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped Risk Location list fetch. Keyed by `lead_id` (never `customer_policy_id`),
// routed through the centralized endpoint map. On failure/timeout it flips the list to
// the Failed load state (retry surfaced by the sub-tab) and shows an error message
// (Requirements 3.1, 3.4, 3.5).
export const fetchLeadBusinessRiskListAsync =
  (
    scope: ILeadRiskLocationScope,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRiskListProgress());
      const request = buildBinderRequest(
        "risk-location",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let finalUrl = request.url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyRiskLocation[] = res.data.data;
      dispatch(fetchBusinessPolicyRiskListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyRiskListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Risk Location binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesPolicyRiskLocationAsync =
  (
    riskLocation: IBusinessPolicyRiskLocation,
    onCallback: (isSuccess: boolean, data?: IBusinessPolicyRiskLocation) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        `/policy/edit-policy-business-risk-location`,
        riskLocation
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Risk location saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped Risk Location save. Persists with `lead_id` and NO `customer_policy_id`
// (the scope keys come from `buildBinderRequest`; the customer-policy identifiers are
// stripped from the record). A local in-flight guard prevents a duplicate save for the
// same record while one is already running (Requirement 3.8). On success it re-fetches
// the list by the same `lead_id` (Requirements 3.2, 3.3, 3.4, 3.7). On failure/timeout
// it surfaces an error; the caller retains the entered input (Requirement 3.6).
export const addLeadBusinessRiskLocationAsync =
  (
    riskLocation: IBusinessPolicyRiskLocation,
    scope: ILeadRiskLocationScope,
    onCallback: (isSuccess: boolean, data?: IBusinessPolicyRiskLocation) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (leadRiskLocationSaveInFlight) {
      return;
    }
    leadRiskLocationSaveInFlight = true;
    try {
      dispatch(saveLoaderProgress());
      const request = buildBinderRequest(
        "risk-location",
        { kind: "lead", leadId: scope.leadId },
        "save"
      );
      const {
        customer_id,
        customer_policy_id,
        ...rest
      } = riskLocation as IBusinessPolicyRiskLocation & {
        customer_id?: unknown;
        customer_policy_id?: unknown;
      };
      const res = await api.post(
        request.url,
        { ...rest, ...request.body },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Risk location saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the list by the same lead_id after a successful save.
      dispatch(fetchLeadBusinessRiskListAsync(scope, status));
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Risk Location could not be saved. Please try again.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      leadRiskLocationSaveInFlight = false;
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_BUSINESS_POLICY_RISK_LOCATION_PROGRESS =
  "FETCH_BUSINESS_POLICY_RISK_LOCATION_PROGRESS";
export const FETCH_BUSINESS_POLICY_RISK_LOCATION_SUCCESS =
  "FETCH_BUSINESS_POLICY_RISK_LOCATION_SUCCESS";
export const FETCH_BUSINESS_POLICY_RISK_LOCATION_FAILED =
  "FETCH_BUSINESS_POLICY_RISK_LOCATION_FAILED";

export const fetchBusinessPolicyRiskProgress = () =>
  action(FETCH_BUSINESS_POLICY_RISK_LOCATION_PROGRESS);
export const fetchBusinessPolicyRiskSuccess = (
  data: IBusinessPolicyRiskLocation
) => action(FETCH_BUSINESS_POLICY_RISK_LOCATION_SUCCESS, { data });
export const fetchBusinessPolicyRiskFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_RISK_LOCATION_FAILED, { errorMessage });

export const fetchBusinessPolicyRiskAsync =
  (
    customerPolicyId: number,
    riskLocationNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRiskProgress());
      const res = await api.get(
        `/policy/get-policy-business-risk-location?customer_policy_id=${customerPolicyId}&column=location_number&value=${riskLocationNumber}`
      );
      const data: IBusinessPolicyRiskLocation[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyRiskSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyRiskFailed(
            "Oops! We couldn't find any records associated with your risk location number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyRiskFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped single Risk Location fetch (by location number). Keyed by `lead_id`
// (never `customer_policy_id`) via the centralized endpoint map (Requirements 3.1,
// 3.4). On failure/timeout it flips the record to the Failed load state so the sub-tab
// can surface the error + retry (Requirement 3.5).
export const fetchLeadBusinessRiskAsync =
  (
    scope: ILeadRiskLocationScope,
    riskLocationNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRiskProgress());
      const request = buildBinderRequest(
        "risk-location",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const finalUrl = `${request.url}&column=location_number&value=${riskLocationNumber}`;
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyRiskLocation[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyRiskSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyRiskFailed(
            "Oops! We couldn't find any records associated with your risk location number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyRiskFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Risk Location binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_BUSINES_POLICY_RISK_LOCATION_STATE =
  "CLEAR_BUSINES_POLICY_RISK_LOCATION_STATE";
export const CLEAR_BUSINES_POLICY_RISK_LOCATION =
  "CLEAR_BUSINES_POLICY_RISK_LOCATION";

export const clearBusinessPolicyRiskState = () =>
  action(CLEAR_BUSINES_POLICY_RISK_LOCATION_STATE);

export const clearBusinessPolicyCRisk = () =>
  action(CLEAR_BUSINES_POLICY_RISK_LOCATION);
